import { createFileRoute } from "@tanstack/react-router"
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Button,
  TextInput,
  Select,
  Tabs,
  useMantineColorScheme,
  Center,
  Box,
  Grid,
  Timeline,
} from "@mantine/core"
import {
  IconSearch,
  IconUsers,
  IconGavel,
  IconFilter,
  IconShieldCheck,
  IconClock,
  IconX,
  IconExternalLink,
  IconAlertTriangle,
  IconCheck,
  IconScale,
} from "@tabler/icons-react"
import { useState, useMemo } from "react"
import {
  getAllSenators,
  getAllRepresentatives,
  searchCongressMembers,
  filterCongressMembers,
  congressSummary,
} from "@/data/congress-full-data"
import type { Party, TransparencyRecord } from "@/types/congress"
import { SEO } from "@/components/seo/SEO"
import { CongressMemberCard } from "@/components/congress/CongressMemberCard"
import { getTransparencyRecord } from "@/lib/transparency-record"

export const Route = createFileRoute("/congress/")({
  component: CongressTrackerPage,
})

function CongressTrackerPage() {
  const { colorScheme } = useMantineColorScheme()
  const [searchQuery, setSearchQuery] = useState("")
  const [activeTab, setActiveTab] = useState("all")
  const [recordFilter, setRecordFilter] = useState<TransparencyRecord | "">("")
  const [partyFilter, setPartyFilter] = useState<Party | "">("")
  const [stateFilter, setStateFilter] = useState("")

  const earlyChampionCount = congressSummary.senate.supporting + congressSummary.house.supporting
  const lateSupporterCount =
    congressSummary.senate.opposing +
    congressSummary.house.opposing +
    congressSummary.senate.unknown +
    congressSummary.house.unknown -
    1 // Subtract Clay Higgins
  const soleOpponentCount = 1

  const baseMembers = useMemo(() => {
    switch (activeTab) {
      case "senate":
        return getAllSenators()
      case "house":
        return getAllRepresentatives()
      default:
        return [...getAllSenators(), ...getAllRepresentatives()]
    }
  }, [activeTab])

  const filteredMembers = useMemo(() => {
    const searchedMembers = searchQuery.trim()
      ? searchCongressMembers(searchQuery, activeTab === "all" ? undefined : (activeTab as "senate" | "house"))
      : baseMembers

    const filters: { party?: Party; state?: string } = {}
    if (partyFilter) filters.party = partyFilter
    if (stateFilter) filters.state = stateFilter

    const afterBasicFilters =
      Object.keys(filters).length > 0 ? filterCongressMembers(searchedMembers, filters) : searchedMembers

    if (!recordFilter) return afterBasicFilters

    return afterBasicFilters.filter((member) => getTransparencyRecord(member) === recordFilter)
  }, [baseMembers, searchQuery, activeTab, recordFilter, partyFilter, stateFilter])

  return (
    <>
      <SEO
        title="Congressional Transparency Record"
        description="The Epstein Files Transparency Act is now law. See every member of Congress's transparency record — who championed the cause early, who came around late, and the sole vote against."
        canonical="https://rtefn.com/congress"
      />
      <div
        style={{
          background:
            colorScheme === "dark"
              ? "linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%)"
              : "linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)",
          minHeight: "100vh",
        }}
      >
        <Container size="xl" py="xl">
          {/* Header */}
          <div className="text-center mb-12">
            <Group justify="center" mb="md">
              <IconScale size={48} color={colorScheme === "dark" ? "#3b82f6" : "#1e40af"} />
            </Group>

            <Title
              order={1}
              size="3rem"
              fw={700}
              ta="center"
              mb="md"
              style={{
                color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a",
                letterSpacing: "-0.02em",
              }}
            >
              Congressional Transparency Record
            </Title>

            <Text
              size="xl"
              ta="center"
              style={{
                color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#64748b",
                maxWidth: "750px",
                margin: "0 auto",
                lineHeight: 1.6,
              }}
            >
              The Epstein Files Transparency Act is now law. This page records every member's transparency record — who
              championed the cause early, who came around late, and who stood alone in opposition.
            </Text>
          </div>

          {/* Victory Banner */}
          <Card
            shadow="lg"
            radius="xl"
            p="lg"
            mb="xl"
            style={{
              background:
                colorScheme === "dark"
                  ? "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(34, 197, 94, 0.15) 100%)"
                  : "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(34, 197, 94, 0.1) 100%)",
              border: "2px solid #10b981",
            }}
          >
            <Group justify="center" align="center" gap="md">
              <IconCheck size={32} color="#10b981" />
              <div style={{ textAlign: "center", flex: 1 }}>
                <Title order={3} style={{ color: "#10b981", marginBottom: "0.5rem" }}>
                  Epstein Files Transparency Act — Signed Into Law November 19, 2025
                </Title>
                <Text
                  size="lg"
                  fw={600}
                  style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a", marginBottom: "0.5rem" }}
                >
                  House: 427-1 | Senate: Unanimous
                </Text>
                <Button
                  component="a"
                  href="/legislative"
                  variant="light"
                  color="green"
                  size="sm"
                  rightSection={<IconExternalLink size={16} />}
                >
                  View Legislative History
                </Button>
              </div>
              <IconCheck size={32} color="#10b981" />
            </Group>
          </Card>

          {/* DOJ Compliance Alert */}
          <Card
            shadow="lg"
            radius="xl"
            p="lg"
            mb="xl"
            style={{
              background:
                colorScheme === "dark"
                  ? "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(245, 158, 11, 0.15) 100%)"
                  : "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)",
              border: "2px solid #ef4444",
              animation: "pulse 2s ease-in-out infinite",
            }}
          >
            <Group justify="center" align="center" gap="md">
              <IconAlertTriangle size={32} color="#ef4444" />
              <div style={{ textAlign: "center", flex: 1 }}>
                <Title order={3} style={{ color: "#ef4444", marginBottom: "0.5rem" }}>
                  DOJ HAS RELEASED LESS THAN 1% OF FILES
                </Title>
                <Text
                  size="md"
                  fw={500}
                  style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a", marginBottom: "0.75rem" }}
                >
                  Over 2 million documents remain unreleased. The law requires full disclosure.
                </Text>
                <Group justify="center" gap="md">
                  <Button
                    component="a"
                    href="/action"
                    variant="filled"
                    color="red"
                    size="sm"
                    rightSection={<IconExternalLink size={16} />}
                  >
                    Demand DOJ Compliance
                  </Button>
                  <Button
                    component="a"
                    href="/legislative"
                    variant="light"
                    color="orange"
                    size="sm"
                    rightSection={<IconExternalLink size={16} />}
                  >
                    Track Legislative Progress
                  </Button>
                </Group>
              </div>
              <IconAlertTriangle size={32} color="#ef4444" />
            </Group>
          </Card>

          {/* Stats Grid */}
          <Grid mb="xl">
            <Grid.Col span={{ base: 12, md: 3 }}>
              <Card
                shadow="lg"
                radius="xl"
                p="lg"
                style={{
                  background: colorScheme === "dark" ? "rgba(16, 185, 129, 0.1)" : "rgba(16, 185, 129, 0.05)",
                  border: "1px solid rgba(16, 185, 129, 0.2)",
                }}
              >
                <Group justify="center" mb="sm">
                  <IconShieldCheck size={32} color="#10b981" />
                </Group>
                <Text size="2rem" fw={700} ta="center" c="#10b981">
                  {earlyChampionCount}
                </Text>
                <Text
                  size="sm"
                  ta="center"
                  style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b" }}
                >
                  Early Champions
                </Text>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 3 }}>
              <Card
                shadow="lg"
                radius="xl"
                p="lg"
                style={{
                  background: colorScheme === "dark" ? "rgba(245, 158, 11, 0.1)" : "rgba(245, 158, 11, 0.05)",
                  border: "1px solid rgba(245, 158, 11, 0.2)",
                }}
              >
                <Group justify="center" mb="sm">
                  <IconClock size={32} color="#f59e0b" />
                </Group>
                <Text size="2rem" fw={700} ta="center" c="#f59e0b">
                  {lateSupporterCount}
                </Text>
                <Text
                  size="sm"
                  ta="center"
                  style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b" }}
                >
                  Late Supporters
                </Text>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 3 }}>
              <Card
                shadow="lg"
                radius="xl"
                p="lg"
                style={{
                  background: colorScheme === "dark" ? "rgba(239, 68, 68, 0.1)" : "rgba(239, 68, 68, 0.05)",
                  border: "1px solid rgba(239, 68, 68, 0.2)",
                }}
              >
                <Group justify="center" mb="sm">
                  <IconX size={32} color="#ef4444" />
                </Group>
                <Text size="2rem" fw={700} ta="center" c="#ef4444">
                  {soleOpponentCount}
                </Text>
                <Text
                  size="sm"
                  ta="center"
                  style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b" }}
                >
                  Sole Opponent
                </Text>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 3 }}>
              <Card
                shadow="lg"
                radius="xl"
                p="lg"
                style={{
                  background: colorScheme === "dark" ? "rgba(59, 130, 246, 0.1)" : "rgba(59, 130, 246, 0.05)",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                }}
              >
                <Group justify="center" mb="sm">
                  <IconGavel size={32} color="#3b82f6" />
                </Group>
                <Text size="2rem" fw={700} ta="center" c="#3b82f6">
                  427-1
                </Text>
                <Text
                  size="sm"
                  ta="center"
                  style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b" }}
                >
                  House Vote / Senate Unanimous
                </Text>
              </Card>
            </Grid.Col>
          </Grid>

          {/* Explanatory Note */}
          <Text
            size="sm"
            ta="center"
            mb="xl"
            style={{
              color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.6)" : "#64748b",
              maxWidth: "800px",
              margin: "0 auto 2rem auto",
              lineHeight: 1.6,
            }}
          >
            Early Champions supported transparency before it was politically convenient — signing the discharge petition
            or voting for the Schumer Amendment when it failed 51-49. Late Supporters voted against early measures but
            ultimately supported the final bill.
          </Text>

          {/* The Record - Timeline */}
          <Card
            shadow="lg"
            radius="xl"
            p="xl"
            mb="xl"
            style={{
              background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(20px)",
              border: colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
            }}
          >
            <Title order={3} mb="lg" ta="center" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
              The Record
            </Title>
            <Timeline active={4} bulletSize={24} lineWidth={2}>
              <Timeline.Item
                bullet={<IconScale size={14} />}
                title={
                  <Text fw={600} style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                    Schumer Amendment Fails 51-49
                  </Text>
                }
              >
                <Text size="sm" style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b" }}>
                  September 10, 2025
                </Text>
              </Timeline.Item>
              <Timeline.Item
                bullet={<IconShieldCheck size={14} />}
                title={
                  <Text fw={600} style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                    Discharge Petition Reaches 218 Signatures
                  </Text>
                }
              >
                <Text size="sm" style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b" }}>
                  November 12, 2025
                </Text>
              </Timeline.Item>
              <Timeline.Item
                bullet={<IconCheck size={14} />}
                title={
                  <Text fw={600} style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                    House Passes 427-1
                  </Text>
                }
              >
                <Text size="sm" style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b" }}>
                  November 18, 2025
                </Text>
              </Timeline.Item>
              <Timeline.Item
                bullet={<IconCheck size={14} />}
                title={
                  <Text fw={600} style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                    Senate Passes Unanimously — Signed Into Law
                  </Text>
                }
              >
                <Text size="sm" style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b" }}>
                  November 19, 2025
                </Text>
              </Timeline.Item>
              <Timeline.Item
                bullet={<IconAlertTriangle size={14} />}
                color="red"
                title={
                  <Text fw={600} style={{ color: "#ef4444" }}>
                    DOJ Has Released Less Than 1% of Files
                  </Text>
                }
              >
                <Text size="sm" style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b" }}>
                  January 2026
                </Text>
              </Timeline.Item>
            </Timeline>
          </Card>

          {/* Search and Filters */}
          <Card
            shadow="lg"
            radius="xl"
            p="lg"
            mb="xl"
            style={{
              background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(20px)",
              border: colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
            }}
          >
            <Stack gap="md">
              <Group gap="md">
                <TextInput
                  placeholder="Search by name, state, or party..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.currentTarget.value)}
                  leftSection={<IconSearch size={16} />}
                  flex={1}
                />
                <Button
                  leftSection={<IconFilter size={16} />}
                  variant="light"
                  onClick={() => {
                    setSearchQuery("")
                    setRecordFilter("")
                    setPartyFilter("")
                    setStateFilter("")
                  }}
                >
                  Clear Filters
                </Button>
              </Group>

              <Group gap="md">
                <Select
                  placeholder="Transparency Record"
                  value={recordFilter}
                  onChange={(value) => setRecordFilter((value || "") as TransparencyRecord | "")}
                  data={[
                    { value: "", label: "All Records" },
                    { value: "early-champion", label: "Early Champions" },
                    { value: "late-supporter", label: "Late Supporters" },
                    { value: "sole-opponent", label: "Sole Opponent" },
                  ]}
                  style={{ width: 180 }}
                />
                <Select
                  placeholder="Party"
                  value={partyFilter}
                  onChange={(value) => setPartyFilter((value || "") as Party | "")}
                  data={[
                    { value: "", label: "All Parties" },
                    { value: "Republican", label: "Republican" },
                    { value: "Democratic", label: "Democratic" },
                    { value: "Independent", label: "Independent" },
                  ]}
                  style={{ width: 150 }}
                />
                <Select
                  placeholder="State"
                  value={stateFilter}
                  onChange={(value) => {
                    if (value !== null) {
                      setStateFilter(value)
                    } else {
                      setStateFilter("")
                    }
                  }}
                  data={[
                    { value: "", label: "All States" },
                    ...Array.from(new Set(baseMembers.map((m) => m.state)))
                      .sort()
                      .map((state) => ({ value: state, label: state })),
                  ]}
                  style={{ width: 150 }}
                  searchable
                />
              </Group>
            </Stack>
          </Card>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={(value) => setActiveTab(value || "all")} mb="xl">
            <Tabs.List grow>
              <Tabs.Tab value="all" leftSection={<IconUsers size={16} />}>
                All Congress ({baseMembers.length})
              </Tabs.Tab>
              <Tabs.Tab value="senate" leftSection={<IconGavel size={16} />}>
                Senate ({getAllSenators().length})
              </Tabs.Tab>
              <Tabs.Tab value="house" leftSection={<IconUsers size={16} />}>
                House ({getAllRepresentatives().length})
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>

          {/* Results */}
          {filteredMembers.length === 0 ? (
            <Center py="xl">
              <Box ta="center">
                <Text size="lg" fw={500} mb="sm" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                  No members found
                </Text>
                <Text style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b" }}>
                  Try adjusting your search or filters
                </Text>
              </Box>
            </Center>
          ) : (
            <>
              <Text
                size="sm"
                mb="lg"
                style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b" }}
              >
                Showing {filteredMembers.length} member{filteredMembers.length !== 1 ? "s" : ""}
              </Text>

              <Grid gutter="md">
                {filteredMembers.map((member) => (
                  <Grid.Col key={member.id} span={{ base: 12, sm: 6, md: 4, lg: 3 }}>
                    <CongressMemberCard member={member} />
                  </Grid.Col>
                ))}
              </Grid>
            </>
          )}

          {/* Bottom CTA */}
          <Card
            shadow="lg"
            radius="xl"
            p="xl"
            mt="xl"
            style={{
              background:
                colorScheme === "dark"
                  ? "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)"
                  : "linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(245, 158, 11, 0.05) 100%)",
              border: colorScheme === "dark" ? "1px solid rgba(239, 68, 68, 0.2)" : "1px solid rgba(239, 68, 68, 0.1)",
            }}
          >
            <div className="text-center">
              <Title order={3} mb="md" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                The Law Passed. Now Demand It Be Followed.
              </Title>

              <Text
                mb="lg"
                style={{
                  color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#64748b",
                  maxWidth: "600px",
                  margin: "0 auto 1.5rem auto",
                  lineHeight: 1.6,
                }}
              >
                The Epstein Files Transparency Act passed with near-unanimous support. The DOJ has released less than 1%
                of the required documents. Hold them accountable.
              </Text>

              <Group justify="center" gap="md">
                <Button
                  size="lg"
                  color="red"
                  variant="filled"
                  component="a"
                  href="/action"
                  rightSection={<IconExternalLink size={16} />}
                >
                  Demand DOJ Compliance
                </Button>
                <Button
                  size="lg"
                  variant="light"
                  component="a"
                  href="/documents"
                  rightSection={<IconExternalLink size={16} />}
                >
                  View Released Documents
                </Button>
              </Group>
            </div>
          </Card>
        </Container>
      </div>
    </>
  )
}
